import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Lock, Landmark, Users, IdCard, Plus, Trash2, Eye, Upload, Loader2, Save, AlertTriangle } from 'lucide-react';
import { PuertaSegundoFactor, useVentanaSegundoFactor } from '../ui-v2.5';
import {
  fetchDatosRrhh, guardarDatosRrhh, anadirHijo, quitarHijo,
  fetchDocumentoIdentidad, subirDocumentoIdentidad, abrirBlob,
} from '../../../peticiones_API/PeticionesFichaRrhh';

/**
 * Fase 12 · §6.7 — la pestaña de RRHH.
 *
 * ── LA VENTANA ES DEL USUARIO, NO DE LA PANTALLA ──────────────────────────────────────────
 *
 * Quien viene con la ventana abierta de Soporte **entra sin que le pidan nada**. No es un atajo:
 * es lo que decidió A1 y lo que hace coherente el mecanismo — el segundo factor prueba quién eres,
 * y eso no caduca al cambiar de pestaña. Lo que cubre el hueco es que **cada consulta se
 * registra**, no la fricción.
 *
 * ── Y AL AGOTARSE, LOS DATOS DESAPARECEN DEL ESTADO ───────────────────────────────────────
 *
 * No se ocultan: **se borran**. Ocultarlos los dejaría en memoria, a un F12 de distancia, y un
 * contador que llega a cero sin hacer nada es peor que no tenerlo porque promete algo que no
 * cumple. Es exactamente lo que hace Soporte desde A1.
 *
 * ── DOS PERMISOS ──────────────────────────────────────────────────────────────────────────
 *
 * Ver y editar son distintos. Aquí sólo se pintan los botones en consecuencia, y eso **no es la
 * barrera**: esconder «Guardar» no impide un PUT. Quien decide es el servidor.
 *
 * (El mockup decía «requiere tu contraseña». Eso es de antes de A1: ahora es el segundo factor,
 * que sustituye a la contraseña propia en vez de sumarse — la contraseña es justo lo que puede
 * estar comprometido.)
 */

const ESTADOS_CIVILES = ['', 'Soltero/a', 'Casado/a', 'Pareja de hecho', 'Separado/a', 'Divorciado/a', 'Viudo/a'];

/**
 * Los años, en un desplegable y no en texto libre: el campo admitía «20015».
 *
 * De más reciente a más antiguo, que es el orden en que se busca. Y **el tope se calcula**, no se
 * escribe: un año fijo empezaría a mentir el 1 de enero.
 *
 * El desplegable es una comodidad, **no la barrera**: el servidor valida el mismo rango, porque
 * esconder una opción no es prohibirla y un año absurdo entraría igual por la API.
 */
const anios = (desde) => {
  const hasta = new Date().getFullYear();
  return Array.from({ length: hasta - desde + 1 }, (_, i) => hasta - i);
};
const ANIOS_HIJO = anios(1940);

/** El panel cerrado. Mismo lenguaje que el de Soporte: qué hay detrás y cómo se abre. */
function Bloqueado({ onAbrir }) {
  return (
    <div className="rounded-v25-lg border border-v25-line bg-v25-surface-sunken p-8 text-center">
      <Lock size={40} className="mx-auto mb-3 text-v25-ink-400" />
      <h5 className="mb-1 text-[15px] font-semibold text-v25-ink-900">Datos confidenciales de RRHH</h5>
      <p className="mx-auto mb-4 max-w-[520px] text-[13px] leading-relaxed text-v25-ink-700">
        IBAN, número de la Seguridad Social, situación familiar, hijos e imágenes del DNI.
        Requiere el permiso correspondiente y tu <strong>verificación en dos pasos</strong>.
      </p>
      <button
        type="button"
        onClick={onAbrir}
        className="inline-flex items-center gap-2 rounded-v25-md bg-v25-brand-800 px-4 py-2 text-[13px] font-semibold text-white shadow-v25-btn"
      >
        <Lock size={15} />
        Desbloquear
      </button>
      <p className="mt-4 text-[12px] text-v25-ink-500">
        Cada consulta queda registrada con quién, qué campos y cuándo.
      </p>
    </div>
  );
}

function Seccion({ icono: Icono, titulo, children }) {
  return (
    <section className="mb-4 rounded-v25-lg border border-v25-line bg-white p-4">
      <header className="mb-3 flex items-center gap-2 border-b border-v25-line pb-2">
        <Icono size={17} className="text-v25-brand-600" />
        <h4 className="text-[14px] font-semibold text-v25-ink-900">{titulo}</h4>
      </header>
      {children}
    </section>
  );
}

const Campo = ({ etiqueta, children }) => (
  <label className="block">
    <span className="mb-1 block text-[12px] font-semibold text-v25-ink-700">{etiqueta}</span>
    {children}
  </label>
);

const claseInput = 'w-full rounded-v25-md border border-v25-line bg-white px-3 py-2 text-[13px] text-v25-ink-900 disabled:bg-v25-surface-sunken disabled:text-v25-ink-500';

export function PestanaRrhh({
  personaId, tipo = null, puedeEditar = false, clave = 'rrhh',
  alCambiar = null, registrarGuardado = null,
}) {
  const ventana2FA = useVentanaSegundoFactor();
  const [puertaAbierta, setPuertaAbierta] = useState(false);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [anioNuevo, setAnioNuevo] = useState('');
  // «Esto ya lo pedí» NO se pinta, así que no es estado: es un `ref`. Con `useState` sería
  // dependencia del efecto que lo escribe, y el re-render cancelaría la petición en vuelo.
  const pedidoPara = useRef(null);

  const cargar = useCallback(async (token) => {
    setCargando(true);
    try {
      setDatos(await fetchDatosRrhh(personaId, token, tipo));
    } catch (e) {
      const msg = e?.response?.data?.message || 'No se pudieron cargar los datos de RRHH.';
      toast.error(msg);
      setDatos(null);
      pedidoPara.current = null;   // que un fallo no bloquee el siguiente intento
    } finally {
      setCargando(false);
    }
  }, [personaId, tipo]);

  // Con la ventana ya abierta —venga de donde venga— se carga sin pedir nada.
  useEffect(() => {
    if (!ventana2FA.activa || !personaId) return;
    if (pedidoPara.current === ventana2FA.ventana) return;
    pedidoPara.current = ventana2FA.ventana;
    cargar(ventana2FA.ventana);
  }, [ventana2FA.activa, ventana2FA.ventana, personaId, cargar]);

  /**
   * Al agotarse la ventana, los datos SE BORRAN del estado.
   *
   * Ocultarlos los dejaría en memoria. Y se avisa, porque una pantalla que se vacía sin decir por
   * qué parece un fallo.
   */
  useEffect(() => {
    if (ventana2FA.activa || !datos) return;
    setDatos(null);
    pedidoPara.current = null;
    toast.info('La verificación ha caducado. Los datos de RRHH se han ocultado.');
  }, [ventana2FA.activa, datos]);

  const cambiar = (campo, valor) => setDatos((d) => ({ ...d, [campo]: valor }));

  /* ─────────────────────────────────────────────────────────────────────────────────────────
   * Fase 12 · §6.9 — cambios sin guardar
   *
   * Aquí SÍ hay instantánea, al revés que en Formación: esto es un formulario de edición, y
   * comparar contra «vacío» daría cambios sin guardar nada más abrir la puerta.
   *
   * Se toma DESPUÉS de que `datos` llegue, no dentro de `cargar`: así incluye lo que el propio
   * render haya normalizado (un `null` que se pinta como `''`, por ejemplo). Es el patrón de
   * `RecursoForm` de la Fase B.
   *
   * Los hijos no entran: tienen endpoint propio y se guardan al pulsar, no al salir. Lo que
   * cuenta es lo que un botón todavía no se ha llevado.
   * ───────────────────────────────────────────────────────────────────────────────────────── */
  const inicial = useRef(null);
  const estadoActual = useMemo(() => (datos ? JSON.stringify({
    iban: datos.iban || '',
    num_seguridad_social: datos.num_seguridad_social || '',
    estado_civil: datos.estado_civil || '',
  }) : null), [datos]);

  useEffect(() => {
    // Sin datos —puerta cerrada o ventana caducada— la referencia se descarta. Si se quedara,
    // al reabrir se compararía contra la instantánea de la vez anterior.
    if (estadoActual === null) { inicial.current = null; return; }
    if (inicial.current === null) inicial.current = estadoActual;
  }, [estadoActual]);

  const hayCambios = inicial.current !== null && estadoActual !== null
    && inicial.current !== estadoActual;

  useEffect(() => {
    if (alCambiar) alCambiar(clave, hayCambios);
  }, [alCambiar, clave, hayCambios]);

  useEffect(() => () => { if (alCambiar) alCambiar(clave, false); }, [alCambiar, clave]);

  useEffect(() => {
    if (!hayCambios) return undefined;
    const alSalir = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [hayCambios]);

  /**
   * Fase 12 · §6.10 — la llama el «Guardar» de la ficha, no un botón propio.
   *
   * **Devuelve `true` si guardó y `false` si falló.** De eso depende que «Guardar y Salir» salga:
   * salir tras un error se llevaría por delante lo escrito.
   *
   * Y devuelve `true` también cuando NO HAY NADA QUE GUARDAR —puerta cerrada, sin permiso de
   * edición, o nada tocado—. No es lo mismo que fallar: pulsar «Guardar y Salir» en una pestaña
   * intacta tiene que salir, como en cualquier otra.
   */
  const guardar = async () => {
    if (!puedeEditar || !datos || !ventana2FA.activa) return true;
    if (!hayCambios) return true;
    setGuardando(true);
    try {
      await guardarDatosRrhh(personaId, ventana2FA.ventana, {
        iban: datos.iban, num_seguridad_social: datos.num_seguridad_social,
        estado_civil: datos.estado_civil,
      }, tipo);
      // La instantánea se mueve a lo que se acaba de guardar, y SÓLO si el servidor dijo que sí:
      // moverla antes dejaría de avisar de unos cambios que no han llegado a ninguna parte.
      inicial.current = estadoActual;
      if (alCambiar) alCambiar(clave, false);
      toast.success('Datos de RRHH guardados.');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudieron guardar los datos.');
      return false;
    } finally {
      setGuardando(false);
    }
  };

  // Se registra en el armazón para que el botón grande pueda llamarla.
  //
  // La referencia se refresca en CADA render y lo que se registra es un envoltorio estable: si se
  // registrara `guardar` directamente, el efecto tendría que volver a correr con cada tecla, y si
  // se registrara una sola vez se quedaría con la `guardar` del primer render —la que ve `datos`
  // en `null`— y no guardaría nunca. Es el mismo fallo de siempre con otra cara: **una copia que
  // deja de corresponderse con el original, sin dar error.**
  const guardarRef = useRef(null);
  guardarRef.current = guardar;
  useEffect(() => {
    if (!registrarGuardado) return undefined;
    registrarGuardado(clave, () => guardarRef.current());
    return () => registrarGuardado(clave, null);
  }, [registrarGuardado, clave]);

  const agregarHijo = async () => {
    const anio = Number(anioNuevo);
    if (!anio) return;
    try {
      const h = await anadirHijo(personaId, ventana2FA.ventana, anio, tipo);
      setDatos((d) => ({ ...d, hijos: [...(d.hijos || []), h].sort((a, b) => a.anio_nacimiento - b.anio_nacimiento) }));
      setAnioNuevo('');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudo añadir.');
    }
  };

  const eliminarHijo = async (hijoId) => {
    try {
      await quitarHijo(personaId, ventana2FA.ventana, hijoId, tipo);
      setDatos((d) => ({ ...d, hijos: (d.hijos || []).filter((h) => h.id !== hijoId) }));
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudo quitar.');
    }
  };

  const [subiendo, setSubiendo] = useState(null);

  /**
   * Subir una cara del DNI. Reemplazar borra la anterior del Blob — lo hace el servidor,
   * best-effort, igual que el borrado de una titulación.
   *
   * Después se relee: el estado dice SI HAY imagen, no cuál, así que tras subir hay que volver a
   * preguntar. Reconstruirlo aquí a mano sería inventar lo que el servidor sabe.
   */
  const subirDni = async (cara, fichero) => {
    if (!fichero) return;
    setSubiendo(cara);
    try {
      const r = await subirDocumentoIdentidad(personaId, ventana2FA.ventana, cara, fichero, tipo);
      await cargar(ventana2FA.ventana);
      toast.success(r?.reemplazado ? `${cara} sustituido.` : `${cara} subido.`);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se pudo subir la imagen.');
    } finally {
      setSubiendo(null);
    }
  };

  const verDni = async (cara) => {
    try {
      abrirBlob(await fetchDocumentoIdentidad(personaId, ventana2FA.ventana, cara, tipo));
    } catch (e) {
      toast.error('No se pudo cargar la imagen.');
    }
  };

  /* ── sin ventana: el panel cerrado y la puerta ──────────────────────────────────────────── */
  if (!ventana2FA.activa || !datos) {
    return (
      <>
        {cargando
          ? <div className="flex items-center justify-center gap-2 p-10 text-[13px] text-v25-ink-500">
              <Loader2 size={16} className="animate-spin" /> Cargando…
            </div>
          : <Bloqueado onAbrir={() => setPuertaAbierta(true)} />}
        {/* La ventana la guarda la PUERTA. Aquí sólo lo de esta pantalla: cargar los datos.
            Antes esto no persistía nada, y el efecto era el peor posible — el código se
            verificaba, la petición devolvía 200, los datos llegaban, y el panel seguía cerrado
            porque `activa` nunca pasaba a true. Ni un error en ninguna parte. */}
        <PuertaSegundoFactor
          open={puertaAbierta}
          onClose={() => setPuertaAbierta(false)}
          onAbierta={(token) => { setPuertaAbierta(false); pedidoPara.current = token; cargar(token); }}
          queVasAVer="los datos de RRHH de esta persona"
        />
      </>
    );
  }

  /* ── con ventana: los cuatro bloques ────────────────────────────────────────────────────── */
  return (
    <div>
      <Seccion icono={Landmark} titulo="Datos económicos">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Campo etiqueta="IBAN">
            <input className={`${claseInput} font-mono`} value={datos.iban || ''} disabled={!puedeEditar}
              onChange={(e) => cambiar('iban', e.target.value)} placeholder="ES00 0000 0000 0000 0000 0000" />
          </Campo>
          <Campo etiqueta="Nº de la Seguridad Social">
            <input className={`${claseInput} font-mono`} value={datos.num_seguridad_social || ''} disabled={!puedeEditar}
              onChange={(e) => cambiar('num_seguridad_social', e.target.value)} />
          </Campo>
        </div>
        <p className="mt-3 text-[12px] leading-relaxed text-v25-ink-500">
          Se guardan cifrados con una clave propia, distinta de la del segundo factor. Se descifran
          sólo para esta pantalla y nunca se escriben en ningún registro.
        </p>
      </Seccion>

      <Seccion icono={Users} titulo="Situación familiar">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Campo etiqueta="Estado civil">
            <select className={claseInput} value={datos.estado_civil || ''} disabled={!puedeEditar}
              onChange={(e) => cambiar('estado_civil', e.target.value)}>
              {ESTADOS_CIVILES.map((x) => <option key={x} value={x}>{x || 'Sin especificar'}</option>)}
            </select>
          </Campo>
        </div>

        <div className="mt-4">
          <span className="mb-2 block text-[12px] font-semibold text-v25-ink-700">
            Hijos {datos.hijos?.length ? `(${datos.hijos.length})` : ''}
          </span>
          {!datos.hijos?.length && (
            <p className="rounded-v25-md border border-dashed border-v25-line px-3 py-2 text-[12px] text-v25-ink-500">
              Sin hijos registrados.
            </p>
          )}
          <ul className="space-y-1">
            {(datos.hijos || []).map((h) => (
              <li key={h.id} className="flex items-center justify-between rounded-v25-md border border-v25-line px-3 py-1.5">
                <span className="text-[13px] text-v25-ink-900">Nacido en {h.anio_nacimiento}</span>
                {puedeEditar && (
                  <button type="button" onClick={() => eliminarHijo(h.id)}
                    aria-label={`Quitar hijo nacido en ${h.anio_nacimiento}`}
                    className="text-v25-ink-500 hover:text-v25-expired-fg">
                    <Trash2 size={15} />
                  </button>
                )}
              </li>
            ))}
          </ul>
          {puedeEditar && (
            <div className="mt-2 flex items-center gap-2">
              <select className={`${claseInput} max-w-[160px]`} aria-label="Año de nacimiento"
                value={anioNuevo} onChange={(e) => setAnioNuevo(e.target.value)}>
                <option value="">Año de nacimiento</option>
                {ANIOS_HIJO.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <button type="button" onClick={agregarHijo} disabled={!anioNuevo}
                className="inline-flex items-center gap-1 rounded-v25-md border border-v25-line bg-white px-3 py-2 text-[13px] font-semibold text-v25-ink-700 shadow-v25-btn disabled:opacity-50">
                <Plus size={14} /> Añadir
              </button>
            </div>
          )}
        </div>
      </Seccion>

      <Seccion icono={IdCard} titulo="Documentos de identidad">
        {/* Las dos caras SIEMPRE, tengan imagen o no: así el sitio donde subirla existe aunque
            todavía no haya nada. Antes sólo se pintaban las que ya estaban, y no había forma de
            añadir la primera. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {['anverso', 'reverso'].map((cara) => {
            const hay = !!datos.documento_identidad?.[cara];
            return (
              <div key={cara} className="rounded-v25-md border border-v25-line p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[12px] font-semibold capitalize text-v25-ink-700">{cara}</span>
                  {hay
                    ? <span className="text-[12px] text-v25-active-fg">Subido</span>
                    : <span className="text-[12px] text-v25-ink-400">Sin imagen</span>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {hay && (
                    <button type="button" onClick={() => verDni(cara)}
                      className="inline-flex items-center gap-2 rounded-v25-md border border-v25-line bg-white px-3 py-1.5 text-[13px] font-semibold text-v25-ink-700 shadow-v25-btn">
                      <Eye size={15} /> Ver
                    </button>
                  )}
                  {puedeEditar && (
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-v25-md border border-v25-line bg-white px-3 py-1.5 text-[13px] font-semibold text-v25-ink-700 shadow-v25-btn">
                      <Upload size={15} />
                      {subiendo === cara ? 'Subiendo…' : (hay ? 'Sustituir' : 'Subir')}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        aria-label={`Subir ${cara} del DNI`}
                        className="hidden"
                        disabled={subiendo === cara}
                        onChange={(e) => { subirDni(cara, e.target.files?.[0]); e.target.value = ''; }}
                      />
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[12px] text-v25-ink-500">
          Sólo PNG, JPG o WEBP, hasta 10&nbsp;MB. Se comprueba el tipo real del fichero, no su
          extensión, y <strong>sustituir una cara borra la anterior</strong>.
          Se sirven por el servidor y no se guardan en la caché del navegador: nunca por un enlace
          público, que sobreviviría a la verificación.
        </p>
      </Seccion>

      {/* Fase 12 · §6.10 — SIN BOTÓN PROPIO.
          Lo guarda el «Guardar» de la ficha, como el resto de la pantalla: **dos botones que
          hacen lo mismo es lo que nos trajo aquí**.
          Lo que queda no es el hueco de un botón quitado: es la franja que dice dónde está el
          que guarda —sin ella, un formulario sin botón parece de sólo lectura— y si hay algo
          escrito todavía sin guardar. */}
      {puedeEditar && (
        <div className="flex flex-wrap items-center gap-3 rounded-v25-lg border border-v25-line bg-v25-surface-sunken px-4 py-3">
          <span className="inline-flex items-center gap-1.5 text-[12px] text-v25-ink-500">
            <Save size={14} />
            Se guarda con <strong className="font-semibold text-v25-ink-700">Guardar</strong> al
            final de la ficha. El IBAN y la Seguridad Social se guardan cifrados; los hijos, al
            añadirlos.
          </span>
          {guardando && (
            <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-v25-ink-700">
              <Loader2 size={14} className="animate-spin" /> Guardando…
            </span>
          )}
          {!guardando && hayCambios && (
            <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-v25-ink-500">
              <AlertTriangle size={14} /> Cambios sin guardar
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default PestanaRrhh;
